import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { apiGet } from "@/lib/api";
import {
  Rss,
  Settings,
  BarChart3,
  KanbanSquare,
  MessageCircle,
  ChevronsUpDown,
  LogOut,
  ShieldCheck,
  Plus,
  Brain,
  Users,
} from "lucide-react";
import LinkedInLabBrand from "@/components/LinkedInLabBrand";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Sidebar — Editorial Light (2026-02).
 * Icon + label rows, active state = colored dot + bold text, no pill chips.
 */
export default function Sidebar({ activeWorkspaceId }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);

  useEffect(() => {
    apiGet("/workspaces").then(setWorkspaces).catch(() => setWorkspaces([]));
  }, []);

  const active = workspaces.find((w) => w.workspace_id === activeWorkspaceId);

  const nav = [
    { to: `/workspace/${activeWorkspaceId}/feed`, label: "Engagement", icon: Rss },
    { to: `/workspace/${activeWorkspaceId}/posts`, label: "Content Board", icon: KanbanSquare },
    { to: `/workspace/${activeWorkspaceId}/outreach`, label: "Outreach", icon: Users },
    { to: `/workspace/${activeWorkspaceId}/brain`, label: "Client Memory", icon: Brain },
    { to: `/workspace/${activeWorkspaceId}`, label: "AI Assistant", icon: MessageCircle, end: true },
    { to: `/workspace/${activeWorkspaceId}/reports`, label: "Reports", icon: BarChart3 },
    { to: `/workspace/${activeWorkspaceId}/settings`, label: "Settings", icon: Settings },
  ];

  return (
    <aside
      data-testid="sidebar"
      className="w-full shrink-0 h-full flex flex-col"
      style={{ background: "#fafafa" }}
    >
      <div className="px-6 pt-8 pb-6">
        <Link to="/dashboard" className="block group" data-testid="sidebar-brand">
          <LinkedInLabBrand variant="full" size="md" mood="cheer" />
        </Link>
      </div>

      {activeWorkspaceId && (
        <div className="px-4 pb-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="workspace-switcher"
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-white hover:bg-white transition-shadow"
                style={{ boxShadow: "0 1px 2px rgba(17,24,39,0.05)" }}
              >
                <div className="text-left overflow-hidden">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9ca3af]">
                    Client
                  </div>
                  <div className="text-sm text-[#111827] truncate font-semibold">
                    {active?.name || "Select a client"}
                  </div>
                </div>
                <ChevronsUpDown className="w-3.5 h-3.5 text-[#9ca3af]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-56 bg-white rounded-xl shadow-lg border-0"
              data-testid="workspace-switcher-menu"
            >
              <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9ca3af]">
                Clients
              </DropdownMenuLabel>
              {workspaces.map((w) => (
                <DropdownMenuItem
                  key={w.workspace_id}
                  onClick={() => navigate(`/workspace/${w.workspace_id}/feed`)}
                  className="text-[#111827] focus:bg-[#f3f4f6] focus:text-[#111827] cursor-pointer rounded-lg"
                  data-testid={`workspace-option-${w.workspace_id}`}
                >
                  {w.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-transparent" />
              {user?.role === "admin" && (
                <DropdownMenuItem
                  onClick={() => navigate("/onboarding")}
                  className="text-[#111827] focus:bg-[#f3f4f6] focus:text-[#111827] cursor-pointer rounded-lg"
                  data-testid="create-workspace-action"
                >
                  <Plus className="w-3.5 h-3.5 mr-2" /> Add a new client
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => navigate("/dashboard")}
                className="text-[#111827] focus:bg-[#f3f4f6] focus:text-[#111827] cursor-pointer rounded-lg"
                data-testid="back-to-dashboard"
              >
                All clients overview
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <nav aria-label="Workspace" className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        <NavLink to="/dashboard" end className={({ isActive }) => `ed-nav-item ${isActive ? "active" : ""}`}>
          <Users className="w-4 h-4 shrink-0" /> All clients
        </NavLink>
        {activeWorkspaceId &&
          nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `ed-nav-item ${isActive ? "active" : ""}`}
            >
              <n.icon className="w-4 h-4 shrink-0" strokeWidth={2} />
              <span>{n.label}</span>
            </NavLink>
          ))}
      </nav>

      <div className="p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu"
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-[#f3f4f6] transition-colors"
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 object-cover rounded-lg"
                />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-[#eef2ff] flex items-center justify-center text-[#4f46e5] text-sm font-bold">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </div>
              )}
              <div className="text-left min-w-0">
                <div className="text-[13px] text-[#111827] truncate font-semibold">{user?.name}</div>
                <div className="text-[10px] text-[#9ca3af] truncate capitalize tracking-wider uppercase">
                  {user?.role}
                </div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="start"
            className="w-56 bg-white rounded-xl shadow-lg border-0"
          >
            <DropdownMenuLabel className="text-[#6b7280] text-xs">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-transparent" />
            {user?.role === "admin" && (
              <DropdownMenuItem
                onClick={() => navigate("/dashboard")}
                className="text-[#111827] focus:bg-[#f3f4f6] focus:text-[#111827] cursor-pointer rounded-lg"
                data-testid="admin-link"
              >
                <ShieldCheck className="w-3.5 h-3.5 mr-2" /> All clients overview
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={logout}
              className="text-[#111827] focus:bg-[#f3f4f6] focus:text-[#111827] cursor-pointer rounded-lg"
              data-testid="logout-btn"
            >
              <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
